import React from "react";
import { PRECONSULTATION_STEPS } from "../preconsultationSteps";
import type {
  PreconsultationStep,
  PreconsultationStepId,
} from "../preconsultationSteps";
import type { PreconsultationAnswers } from "../types/preconsultation.types";
import { timeLabel } from "./PreconsultationControls";
import { habitLabel } from "../preconsultationSteps";
import {
  BASIC_ROWS,
  SummaryCard,
  UNANSWERED,
  list,
  value,
} from "./PreconsultationSummary";
import type { SummaryRow } from "./PreconsultationSummary";

/**
 * The last screen before Submit: everything that was answered, in the order it
 * was asked, with a way back into each part of the form.
 *
 * The point of it is that this form is long and is filled in on a phone over
 * several minutes, often while doing something else. By the time somebody
 * reaches the end they no longer remember what they put in step two — and the
 * answers are about to become the basis of their diet. So they are shown, in
 * full, before anything is sent.
 *
 * Every section carries an Edit that jumps back to the step it came from
 * rather than to a field. Jumping to a field would need the step to be drawn
 * and scrolled and focused, and would land somebody in the middle of a form
 * with no idea what else is on it; a step is the unit they filled in, so it is
 * the unit they go back to.
 */

/** `168 cm`, or a dash where no figure was given — never a bare unit. */
const measure = (amount: string, unit: string): string =>
  amount.trim() ? `${amount.trim()} ${unit}` : UNANSWERED;

/**
 * A stretch of the day, as it was answered.
 *
 * Read back on the twelve-hour clock the times were picked on — `09:00 am`,
 * not `09:00`. This screen exists to be checked against what somebody
 * remembers choosing, and a morning that comes back as `18:00` is a line they
 * have to translate before they can tell whether it is right.
 *
 * "No fixed hours" is its own line rather than a dash, because it is what
 * somebody actually said — see `TimeRangeField`, which is where that
 * distinction is made and why.
 */
const hours = (from: string, to: string, anyHours: boolean): string => {
  if (anyHours) return "No fixed hours";
  if (!from && !to) return UNANSWERED;
  return `${timeLabel(from) || UNANSWERED} - ${timeLabel(to) || UNANSWERED}`;
};

interface Section {
  title: string;
  /** Which step its Edit goes back to. */
  step: PreconsultationStepId;
  /** The card's own row shape — see `SummaryCard`, which draws them. */
  rows: SummaryRow[];
  /** How many of them stand side by side on a wide screen. */
  columns?: 2 | 3 | 4;
}

const sectionsOf = (answers: PreconsultationAnswers): Section[] => [
  // The card the form opened on, at the end of it and unchanged — see
  // `BASIC_ROWS`, which both screens read.
  { title: "Basic", step: "basics", rows: BASIC_ROWS(answers), columns: 4 },
  {
    title: "Location",
    step: "location",
    rows: [
      { label: "Full address", text: value(answers.address) },
      { label: "City", text: value(answers.locationCity) },
      { label: "State", text: value(answers.state) },
      { label: "Pin code", text: value(answers.pincode) },
    ],
    columns: 4,
  },
  {
    title: "Body",
    step: "body",
    rows: [
      { label: "Height", text: measure(answers.height, answers.heightUnit) },
      { label: "Weight", text: measure(answers.weight, answers.weightUnit) },
      {
        label: "Target weight",
        text: measure(answers.targetWeight, answers.targetWeightUnit),
      },
    ],
    columns: 3,
  },
  {
    title: "Food",
    step: "food",
    rows: [
      { label: "Food preference", text: value(answers.foodPreference) },
      { label: "Favorite cuisines", text: list(answers.cuisines) },
      { label: "Dishes you love", text: list(answers.dishesLoved) },
      { label: "Dishes you avoid", text: list(answers.dishesAvoided) },
      { label: "Food allergies", text: list(answers.foodAllergies) },
    ],
    columns: 3,
  },
  {
    title: "Your day",
    step: "day",
    rows: [
      { label: "Profession", text: value(answers.profession) },
      {
        label: "Working hours",
        text: hours(answers.workFrom, answers.workTo, answers.workAnyHours),
      },
      {
        label: "Sleep timings",
        text: hours(answers.sleepFrom, answers.sleepTo, answers.sleepAnyHours),
      },
      // Read back as the words they were picked by: the answers are held as
      // the backend's constants, and `OCCASIONAL` is not a thing anybody said.
      { label: "Travel frequency", text: value(habitLabel(answers.travel)) },
      { label: "Alcohol", text: value(habitLabel(answers.alcohol)) },
      { label: "Smoking", text: value(habitLabel(answers.smoking)) },
    ],
    columns: 3,
  },
  {
    title: "Health",
    step: "health",
    rows: [
      { label: "Medical conditions", text: list(answers.conditions) },
      {
        label: "Medications",
        // A "No" is worth printing. A dash here would read as a question
        // nobody reached, and this one was answered.
        text:
          answers.onMedication === "No"
            ? "None"
            : list(answers.medications),
      },
    ],
    columns: 2,
  },
];

export interface PreconsultationReviewProps {
  answers: PreconsultationAnswers;
  /**
   * The steps this form actually asked — see `stepsAsked`. A section whose
   * step is not among them is left off entirely rather than printed as a card
   * of dashes, which would read as questions somebody failed to answer.
   */
  steps: PreconsultationStep[];
  onEdit: (step: PreconsultationStepId) => void;
}

const PreconsultationReview: React.FC<PreconsultationReviewProps> = ({
  answers,
  steps,
  onEdit,
}) => (
  <>
    {sectionsOf(answers)
      .filter((section) => steps.some((step) => step.id === section.step))
      .map((section) => (
        <SummaryCard
          key={section.title}
          title={section.title}
          rows={section.rows}
          columns={section.columns}
          onEdit={() => onEdit(section.step)}
          editLabel={`Edit ${section.title.toLowerCase()} — ${
            PRECONSULTATION_STEPS.find((step) => step.id === section.step)
              ?.title ?? ""
          }`}
        />
      ))}
  </>
);

export default PreconsultationReview;
